import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, InputNumber, Button, Empty, Row, Modal } from 'antd';
import { DeleteFilled, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import InputTooltip from 'components/InputInfoCircle';
import Collapse from 'components/SortableCollapse';
import { arrayMoveImmutable } from 'array-move';

import messages from './messages';

const { Panel } = Collapse;

const OPTION_RESPONSE_COUNT = gql`
  query($id: ID!) {
    option(id: $id) { id responseCount }
  }
`;

const RemoveOptionModal = ({ option, onRemove, onCancel }) => {
  const { data, loading } = useQuery(OPTION_RESPONSE_COUNT, { variables: { id: option.id }, skip: !option });

  const responseCount = data?.option?.responseCount;

  const canRemove = responseCount === 0;

  return (
    <Modal
      title={<FormattedMessage {...messages.removeOptionTitle} />}
      open={option}
      onCancel={onCancel}
      onOk={canRemove ? onRemove : onCancel}
      confirmLoading={loading}
    >
      {!loading && (
        canRemove ? (
          <FormattedMessage {...messages.removeOptionNoResponses} values={{ option: option.title }} />
        ) : (
          <FormattedMessage {...messages.removeOptionHasResponses} values={{ option: option.title, responseCount }} />
        )
      )}
    </Modal>

  );
};

const EditOptions = ({ options = [], onChange }) => {
  const { formatMessage: $t } = useIntl();
  const [newOptionTitle, setNewOptionTitle] = useState('');
  const [optionToRemove, setOptionToRemove] = useState(false);

  const handleNewOption = () => {
    if (newOptionTitle) {
      const tempId = Math.random() * 100;
      onChange([...options, { tempId, title: newOptionTitle, active: true }]);
    }
    setNewOptionTitle('');
  };

  const getId = (option) => option.id || option.tempId;

  const handleChange = (option) => {
    const updated = options.filter(o => getId(o) !== getId(option));
    const i = options.findIndex(o => getId(o) === getId(option));
    updated.splice(i, 0, option);

    onChange(updated);
  };

  const onValueChange = option => (value) => {
    const copy = JSON.parse(JSON.stringify(option)); // deep copy
    copy.value = value;

    handleChange(copy);
  };

  const onTitleChange = option => (e) => {
    const copy = JSON.parse(JSON.stringify(option)); // deep copy
    copy.title = e.target.value;

    handleChange(copy);
  };

  const toggle = option => (e) => {
    e.stopPropagation();

    const copy = JSON.parse(JSON.stringify(option)); // deep copy
    copy.active = !option.active;

    handleChange(copy);
  };

  const onRemove = (option) => {
    onChange(options.filter(o => getId(o) !== getId(option)));
    setOptionToRemove(false);
  };

  const remove = option => (e) => {
    e.stopPropagation();
    if (option.tempId) {
      onRemove(option);
    } else {
      setOptionToRemove(option);
    }
  };

  const move = ({ oldIndex, newIndex }) => {
    const sorted = arrayMoveImmutable(options, oldIndex, newIndex);
    onChange(sorted);
  };

  return (
    <>
      <Form.Item>
        <Input
          placeholder={$t(messages.addOptionPlaceholder)}
          value={newOptionTitle}
          onChange={e => setNewOptionTitle(e.target.value)}
          onPressEnter={handleNewOption}
          suffix={<InputTooltip tooltip={<FormattedMessage {...messages.addOptionTooltip} />} />}
        />
      </Form.Item>
      {!options.length > 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<FormattedMessage {...messages.emptyOptions} />}
        />
      )}
      {options.length > 0 && (
        <Collapse onSortEnd={move}>
          {options.map((option, index) => (
            <Panel
              key={getId(option)}
              index={index}
              header={option.title}
              extra={(
                <>
                  <Button
                    size="small"
                    type="link"
                    icon={option.active ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={toggle(option)}
                  />
                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<DeleteFilled />}
                    onClick={remove(option)}
                  />
                </>
              )}
            >
              <Row>
                <Form.Item style={{ flex: 1, marginRight: 16 }} label={<FormattedMessage {...messages.option} />}>
                  <Input value={option.title} onChange={onTitleChange(option)} />
                </Form.Item>
                <Form.Item label={<FormattedMessage {...messages.value} />}>
                  <InputNumber value={option.value} onChange={onValueChange(option)} />
                </Form.Item>
              </Row>
            </Panel>
          ))}
        </Collapse>
      )}
      {optionToRemove && (
        <RemoveOptionModal option={optionToRemove} onRemove={() => onRemove(optionToRemove)} onCancel={() => setOptionToRemove(false)} />
      )}
    </>
  );
};

export default EditOptions;
